import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import useMenu, { MenuItemType } from '~shared/hooks/useMenu';

import { Svg } from '../svg/svg';

interface SidebarProps {
	isOpen: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen }) => {
	const [openItems, setOpenItems] = useState<{ [key: string]: boolean }>({});
	const location = useLocation();
	const menu = useMenu();

	const handleItemClick = (item: MenuItemType, event: React.MouseEvent) => {
		if (item.submenu) {
			setOpenItems(prev => ({
				...prev,
				[item.title]: !prev[item.title],
			}));
			event.preventDefault();
		} else if (item.onClick) {
			item.onClick(event);
		}
	};

	const isItemOpen = (title: string) => openItems[title] || false;
	const isActiveLink = (link?: string) => link && location.pathname === link;

	const renderLink = (item: MenuItemType, isSubmenu: boolean = false) => {
		const className = `
        flex items-center ${isSubmenu ? 'justify-start' : 'justify-between'} 
        px-6 py-4 text-white text-sm uppercase tracking-wide border-l-3 
        transition-all duration-200 hover:bg-white/5
        ${item.active?.() && !item.submenu ? 'border-[#6f6eff] bg-white/5' : 'border-transparent'}
        ${isItemOpen(item.title) ? 'bg-white/5' : ''}
      `;
		const content = (
			<>
				<div className="flex items-center flex-1">
					{item.ico && <Svg name={item.ico} className="mr-4 flex-shrink-0" />}
					<span className="truncate">{item.title}</span>
				</div>
				{!isSubmenu && item.submenu && (
					<svg
						className={`w-4 h-4 transition-transform duration-300 ${
							isItemOpen(item.title) ? 'rotate-180' : ''
						}`}
						viewBox="0 0 12 12"
						fill="currentColor"
					>
						<path d="M6 8.825L1.175 4 2.25 2.925 6 6.675 9.75 2.925 10.825 4z" />
					</svg>
				)}
			</>
		);
		const commonProps = {
			className,
			onClick: (e: React.MouseEvent) => handleItemClick(item, e),
		};

		if (item.external && item.link) {
			return (
				<a href={item.link} {...commonProps}>
					{content}
				</a>
			);
		}

		return (
			<Link to={item.link || '#'} {...commonProps}>
				{content}
			</Link>
		);
	};

	const renderSubmenuLink = (subitem: MenuItemType) => {
		const linkProps = {
			to: subitem.link || '#',
			className: `
        flex items-center px-6 py-3 text-white text-sm border-l-3 
        transition-all duration-200 hover:bg-white/5
        ${isActiveLink(subitem.link) ? 'border-[#6f6eff] bg-white/5' : 'border-transparent'}
      `,
			onClick: (e: React.MouseEvent) => {
				if (subitem.onClick) {
					subitem.onClick(e);
				}
			},
			...(subitem.external && {
				rel: 'noopener noreferrer',
				target: '_blank',
			}),
		};

		return (
			<Link {...linkProps}>
				{subitem.ico && (
					<Svg
						name={subitem.ico}
						className="mr-5 flex-shrink-0"
						// size={16}
						// color={isActiveLink(subitem.link) ? '#6f6eff' : 'rgba(255,255,255,0.5)'}
						// fallback={<div className="w-4 h-4 mr-5 bg-white/30 rounded flex-shrink-0"></div>}
					/>
				)}
				<span
					className={`
          truncate
          ${isActiveLink(subitem.link) ? 'opacity-100 text-white' : 'opacity-50'}
        `}
				>
					{subitem.title}
				</span>
			</Link>
		);
	};

	return (
		<nav
			className={`
       fixed bottom-0 left-0 w-72 h-[calc(100%-70px)] bg-[#3b4069]
        overflow-y-auto transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}
			style={{ zIndex: 1000 }}
		>
			<ul>
				{menu.map(item => (
					<li
						key={item.title}
						className={`
              border-t border-white/10
              ${isActiveLink(item.link) && !item.submenu ? 'bg-white/5' : ''}
            `}
					>
						{renderLink(item)}

						{item.submenu && isItemOpen(item.title) && (
							<div className="bg-[#2d3254]">
								<ul>
									{item.submenu.map(subitem => (
										<li key={subitem.link}>{renderSubmenuLink(subitem)}</li>
									))}
								</ul>
							</div>
						)}
					</li>
				))}
			</ul>
		</nav>
	);
};
